import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaMobileAlt,
  FaShieldAlt,
  FaDatabase,
  FaUserCog
} from 'react-icons/fa';
import '../css/OZMechanicShowcase.css';

import screenLanding from '../assets/oz/landing.png';
import screenLogin from '../assets/oz/login.png';
import screenUserDashboard from '../assets/oz/user-dashboard.png';
import screenServices from '../assets/oz/services.png';
import screenAdminDashboard from '../assets/oz/admin-dashboard.png';
import screenAdminServices from '../assets/oz/admin-services.png';

const features = [
  {
    icon: <FaMobileAlt />,
    title: 'React Native App',
    text: 'Smooth mobile-first user and admin experience.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'JWT Security',
    text: 'Secure authentication and protected workflows.'
  },
  {
    icon: <FaDatabase />,
    title: 'FastAPI + MongoDB',
    text: 'Scalable backend APIs and real-time data structure.'
  },
  {
    icon: <FaUserCog />,
    title: 'Admin Control',
    text: 'Manage services, users, bookings, and visibility.'
  }
];

const screens = [
  {
    image: screenLanding,
    label: 'Landing',
    title: 'Customer-facing landing flow'
  },
  {
    image: screenLogin,
    label: 'Login',
    title: 'Secure sign-in experience'
  },
  {
    image: screenUserDashboard,
    label: 'User',
    title: 'Customer dashboard and quick actions'
  },
  {
    image: screenServices,
    label: 'Services',
    title: 'Service cards and booking flow'
  },
  {
    image: screenAdminDashboard,
    label: 'Admin',
    title: 'Admin dashboard overview'
  },
  {
    image: screenAdminServices,
    label: 'Manager',
    title: 'Service management panel'
  }
];

export default function OZMechanicShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screens.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screens.length) % screens.length);
  };

  const activeScreen = screens[activeIndex];

  return (
    <section className="oz-showcase-section" id="mechanic-finders">
      
      <div className="oz-bg-orb orb-left" />
      <div className="oz-bg-orb orb-right" />
      <div className="oz-grid-overlay" />
      <div className="oz-watermark">MECHANIC FINDERS</div>

      <div className="oz-showcase-container">
        <motion.div
          className="oz-showcase-left"
          initial={{ opacity: 0, x: -45 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <span className="oz-tag">Featured Platform</span>
          <h2> Mechanic Finders App</h2>
          <p>
            A modern automotive service platform built for both customers and admin operators.
            It combines bookings, vehicles, service cards, secure authentication, admin workflows,
            and real business logic into one strong mobile ecosystem.
          </p>

          <div className="oz-feature-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="oz-feature-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <div className="oz-feature-icon">{feature.icon}</div>
                <div>
                  <h4>{feature.title}</h4>
                  <p>{feature.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="oz-stat-row">
            <div className="oz-stat-box">
              <strong>2</strong>
              <span>User + Admin</span>
            </div>
            <div className="oz-stat-box">
              <strong>6+</strong>
              <span>Core Screens</span>
            </div>
            <div className="oz-stat-box">
              <strong>JWT</strong>
              <span>Secured Access</span>
            </div>
          </div>

          <div className="oz-buttons">
            <a
              href="https://oz-mechanic.shop"
              target="_blank"
              rel="noreferrer"
              className="oz-btn oz-btn-primary"
            >
              Visit Website
            </a>

            <a href="#projects" className="oz-btn oz-btn-secondary">
              See More Projects
            </a>
          </div>
        </motion.div>

        <motion.div
          className="oz-showcase-right"
          initial={{ opacity: 0, x: 45 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="oz-screen-panel">
            <div className="oz-screen-topbar">
              <div className="oz-screen-meta">
                <span className="oz-screen-label">{activeScreen.label}</span>
                <h3>{activeScreen.title}</h3>
              </div>

              <div className="oz-screen-controls">
                <button type="button" onClick={prevSlide} aria-label="Previous screen">
                  <FaChevronLeft />
                </button>
                <button type="button" onClick={nextSlide} aria-label="Next screen">
                  <FaChevronRight />
                </button>
              </div>
            </div>

            <div className="oz-phone-stage">
              <motion.div
                className="oz-phone-card oz-phone-back left"
                animate={{ y: [0, -8, 0], rotate: [-8, -10, -8] }}
                transition={{ repeat: Infinity, duration: 5.6, ease: 'easeInOut' }}
              >
                <img
                  src={screens[(activeIndex + 1) % screens.length].image}
                  alt="OZ Mechanic preview background left"
                />
              </motion.div>

              <motion.div
                className="oz-phone-card oz-phone-back right"
                animate={{ y: [0, -10, 0], rotate: [8, 10, 8] }}
                transition={{ repeat: Infinity, duration: 5.9, ease: 'easeInOut' }}
              >
                <img
                  src={screens[(activeIndex + 2) % screens.length].image}
                  alt="OZ Mechanic preview background right"
                />
              </motion.div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  className="oz-phone-card oz-phone-main"
                  initial={{ opacity: 0, y: 30, scale: 0.94, rotate: -2 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, y: -18, scale: 0.96, rotate: 2 }}
                  transition={{ duration: 0.45 }}
                >
                  <div className="oz-phone-notch" />
                  <img
                    src={activeScreen.image}
                    alt={activeScreen.title}
                    className="oz-phone-image"
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="oz-screen-thumbs">
              {screens.map((screen, index) => (
                <button
                  key={screen.label}
                  type="button"
                  className={`oz-thumb ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show ${screen.label}`}
                >
                  <img src={screen.image} alt={screen.label} />
                </button>
              ))}
            </div>

            <a
              href="https://oz-mechanic.shop"
              target="_blank"
              rel="noreferrer"
              className="oz-inline-link"
            >
              Open live platform <FaArrowRight />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
